import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles'
import { Box, Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { heroAiPoweredData } from '@/slices/HomePage/mocks/heroAiPowered/data'

export const HeroAiPowered: React.FC = () => {
  const {
    eyebrow,
    headline,
    headlineAccent,
    subtitle,
    primaryCtaText,
    primaryCtaLink,
    transformations,
  } = heroAiPoweredData

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (transformations.length < 2) return undefined

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % transformations.length)
    }, 6000)

    return () => clearInterval(id)
  }, [transformations.length])

  const active = transformations[activeIndex]

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + transformations.length) % transformations.length)
  }

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % transformations.length)
  }

  return (
    <StyledSection>
      <Container maxWidth="lg">
        <StyledGrid>
          <StyledTextColumn>
            <StyledEyebrow>
              <Icon icon={faSparkles} sx={{ fontSize: pxToRem(14), mr: 1 }} />
              {eyebrow}
            </StyledEyebrow>
            <StyledHeadline variant="h1">
              {headline} <StyledAccent>{headlineAccent}</StyledAccent>
            </StyledHeadline>
            <StyledSubtitle variant="body1">
              {subtitle}
            </StyledSubtitle>
            <StyledCtaRow>
              <StyledPrimaryCta arrow primary size="large" href={primaryCtaLink}>
                {primaryCtaText}
              </StyledPrimaryCta>
            </StyledCtaRow>
            <StyledTrustRow>
              <StyledTrustDot />
              <Typography variant="body2">
                One-time purchase. No subscriptions. Yours to keep forever.
              </Typography>
            </StyledTrustRow>
          </StyledTextColumn>

          <StyledVisualColumn>
            <StyledTransformation>
              <StyledBeforeAfter>
                <StyledImageFrame>
                  <Image
                    src={active.beforeImageUrl}
                    alt={active.beforeAlt}
                    fill
                    priority
                    sizes="(max-width: 768px) 45vw, 280px"
                    style={{ objectFit: 'cover' }}
                  />
                </StyledImageFrame>

                <StyledArrowWrapper>
                  <StyledArrowImage>
                    <Image
                      src="/images/artstyles/arrow.png"
                      alt="Personalisation arrow"
                      fill
                      sizes="80px"
                      style={{ objectFit: 'contain' }}
                    />
                  </StyledArrowImage>
                </StyledArrowWrapper>

                <StyledBookFrame>
                  <Image
                    src={active.afterImageUrl}
                    alt={active.afterAlt}
                    fill
                    priority
                    sizes="(max-width: 768px) 45vw, 280px"
                    style={{
                      objectFit: 'contain',
                      filter: 'drop-shadow(12px 16px 24px rgba(108, 60, 20, 0.35))',
                    }}
                  />
                </StyledBookFrame>
              </StyledBeforeAfter>

              <StyledCaption>{active.caption}</StyledCaption>

              {transformations.length > 1 && (
                <StyledNav>
                  <StyledNavButton
                    onClick={goPrev}
                    aria-label="Previous transformation"
                    type="button"
                  >
                    <Icon icon={faChevronLeft} sx={{ fontSize: pxToRem(14) }} />
                  </StyledNavButton>
                  <StyledDots>
                    {transformations.map((t, idx) => (
                      <StyledDot
                        key={t.id}
                        $active={idx === activeIndex}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`Show transformation ${idx + 1}`}
                        type="button"
                      />
                    ))}
                  </StyledDots>
                  <StyledNavButton
                    onClick={goNext}
                    aria-label="Next transformation"
                    type="button"
                  >
                    <Icon icon={faChevronRight} sx={{ fontSize: pxToRem(14) }} />
                  </StyledNavButton>
                </StyledNav>
              )}
            </StyledTransformation>
          </StyledVisualColumn>
        </StyledGrid>
      </Container>
    </StyledSection>
  )
}

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFFAF6 0%, #FFF0E2 55%, #FFE3D1 100%)',
  padding: theme.spacing(5, 0, 6),
  position: 'relative',
  overflow: 'hidden',

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0, 10),
  },
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(5),
  gridTemplateColumns: '1fr',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 1.15fr',
    gap: theme.spacing(6),
  },
}))

const StyledTextColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2.5),
  textAlign: 'left',
  order: 2,

  [theme.breakpoints.up('md')]: {
    order: 1,
    gap: theme.spacing(3),
  },
}))

const StyledEyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: 'rgba(220, 174, 150, 0.18)',
  color: theme.palette.primary.main,
  padding: theme.spacing(0.75, 1.75),
  borderRadius: pxToRem(999),
  fontSize: pxToRem(13),
  fontWeight: 700,
  letterSpacing: pxToRem(1.2),
  textTransform: 'uppercase',
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(30),
  lineHeight: 1.15,

  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(38),
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
  },
}))

const StyledAccent = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.85,
  fontSize: pxToRem(15),
  lineHeight: 1.55,
  maxWidth: pxToRem(520),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(17),
  },
}))

const StyledCtaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
  marginTop: theme.spacing(1),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 'auto',
  },
}))

const StyledPrimaryCta = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
  padding: theme.spacing(2, 4),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.25, 5),
  },
}))

const StyledTrustRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.primary.main,
  opacity: 0.75,
  marginTop: theme.spacing(1),

  '& .MuiTypography-root': {
    fontSize: pxToRem(13),
  },
}))

const StyledTrustDot = styled('span')(({ theme }) => ({
  width: pxToRem(6),
  height: pxToRem(6),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  opacity: 0.6,
  flexShrink: 0,
}))

const StyledVisualColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  order: 1,

  [theme.breakpoints.up('md')]: {
    order: 2,
  },
}))

const StyledTransformation = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: pxToRem(520),
  padding: theme.spacing(1, 0),

  [theme.breakpoints.up('md')]: {
    maxWidth: pxToRem(640),
  },
}))

const StyledBeforeAfter = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: theme.spacing(1.5),

  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2.5),
  },
}))

const StyledImageFrame = styled('div')({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: pxToRem(24),
  overflow: 'hidden',
  backgroundColor: '#F5EDE5',
  boxShadow: '0 18px 40px rgba(108, 60, 20, 0.18)',
})

const StyledBookFrame = styled('div')({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
})

const StyledArrowWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const StyledArrowImage = styled('div')(({ theme }) => ({
  position: 'relative',
  width: pxToRem(56),
  height: pxToRem(40),
  flexShrink: 0,

  [theme.breakpoints.up('sm')]: {
    width: pxToRem(80),
    height: pxToRem(56),
  },

  [theme.breakpoints.up('md')]: {
    width: pxToRem(96),
    height: pxToRem(68),
  },
}))

const StyledCaption = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: pxToRem(14),
}))

const StyledNav = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1.5),
}))

const StyledNavButton = styled('button')(({ theme }) => ({
  width: pxToRem(32),
  height: pxToRem(32),
  borderRadius: '50%',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  backgroundColor: 'rgba(108, 60, 20, 0.08)',
  color: theme.palette.primary.main,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: 'rgba(108, 60, 20, 0.16)',
  },
}))

const StyledDots = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
}))

const StyledDot = styled('button', { shouldForwardProp: (prop) => prop !== '$active' })<{ $active: boolean }>(({ theme, $active }) => ({
  width: pxToRem(8),
  height: pxToRem(8),
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  backgroundColor: $active ? theme.palette.primary.main : 'rgba(108, 60, 20, 0.2)',
  transition: 'background-color 0.2s ease',
}))
